import React from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import pagesData from '../pages.json';

// ============================================================================
// Dynamic navigation items from pages.json
// ============================================================================

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
}

/**
 * Build navigation links dynamically from pages.json.
 * Includes the home page and all visible, non-template pages.
 */
const NAV_LINKS = Object.entries(pagesData as Record<string, PageEntry>)
  .filter(([_, page]) => {
    if (page.visible === false) return false;
    if ((page as any).template) return false;
    return true;
  })
  .sort((a, b) => a[1].position - b[1].position)
  .map(([_, page]) => ({
    label: page.name,
    href: page.path,
  }));

const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule Tour Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule A Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

export function Navigation() {
  const scheduleTourLink = useSlotGroup('navigation.schedule_tour_link', scheduleTourLinkDef);

  return (
    <nav className="bg-[var(--vibe-color-surface)] border-b border-[var(--vibe-color-border)] sticky top-20 z-40">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-12 overflow-x-auto">
          <div className="flex items-center gap-6">
            {NAV_LINKS.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="font-body text-sm text-[var(--vibe-color-text-muted)] hover:text-[var(--vibe-color-primary)] transition-colors whitespace-nowrap"
              >
                {link.label}
              </a>
            ))}
          </div>

          <EditableSlotGroup groupPath="navigation.schedule_tour_link" definition={scheduleTourLinkDef}>
            <a
              href={scheduleTourLink.url || '/schedule-a-tour'}
              target={scheduleTourLink._target || '_self'}
              className="hidden md:inline-flex items-center px-4 py-1.5 text-xs font-semibold tracking-wider uppercase bg-[var(--vibe-color-primary)] text-white hover:bg-[var(--vibe-color-secondary)] transition-colors whitespace-nowrap"
            >
              {scheduleTourLink.label || 'Schedule A Tour'}
            </a>
          </EditableSlotGroup>
        </div>
      </div>
    </nav>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Navigation',
  displayName: 'Navigation',
  slotGroups: {
    'navigation.schedule_tour_link': scheduleTourLinkDef,
  },
};

(Navigation as any).contentSchema = contentSchema;

export default Navigation;
